import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { HelpHint } from "@/components/help";
import { HELP_KEYS } from "@/lib/help-context";
import { useOrganization } from "@/auth/organization";
import { useStepUp } from "@/components/domains/shared";
import {
  ErrorAlert,
  Form,
  Badge,
  Modal,
  Field,
  Input,
  Button,
  Section,
  type Requirement,
} from "@/components/ui";
import { domainKeys, domainsApi, has, longDate, type DomainDetail, type Operation } from "@/lib/domains";
import { TwoColumns, RuleRow, Notice } from "@/components/domains/parts";
import { useUnsavedChanges } from "@/lib/unsavedChanges";
import { ChildNameserversSection } from "../nameservers/ChildNameservers";

const NS = /^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

function opNameservers(op: Operation | undefined): string[] {
  const list = op?.detail.nameservers;
  return Array.isArray(list) ? list.filter((h): h is string => typeof h === "string") : [];
}

/** Figma 2042-4 "Nameservers" and 4090-8342 "Use custom nameservers". */
export function NameserversTab({ detail }: { detail: DomainDetail }) {
  const d = detail.domain;
  const { orgId, canOn } = useOrganization();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const manage: Requirement = { permission: "domains:manage", on: ["domain_folder", detail.folder?.id] };
  const canManage =
    canOn(manage.permission, "domain_folder", detail.folder?.id) && has(detail, "nameservers");
  const { run, modal: stepUp } = useStepUp();

  const nsOps = detail.operations
    .filter((o) => o.kind === "nameservers")
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  const pending = nsOps.find((o) => o.status === "pending");
  const lastFailed = nsOps[0]?.status === "failed" ? nsOps[0] : undefined;

  // The registry answers asynchronously: ask about the operation until it settles.
  const op = useQuery({
    queryKey: [...domainKeys.detail(orgId, d.id), "operation", pending?.id ?? ""],
    queryFn: () => domainsApi.operation(orgId, d.id, pending!.id),
    enabled: Boolean(pending),
    refetchInterval: (q) => (q.state.data && q.state.data.status !== "pending" ? false : 4000),
  });
  const settled = op.data && op.data.status !== "pending";
  useEffect(() => {
    if (settled) void qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
  }, [settled, qc, orgId]);

  const events = useQuery({
    queryKey: [...domainKeys.all(orgId), "events", d.id, "nameservers"],
    queryFn: () => domainsApi.events(orgId, d.id, "nameservers"),
  });

  const [revertOpen, setRevertOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const customOpen = params.get("modal") === "custom-ns" && canManage;
  const closeCustom = () =>
    setParams((p) => {
      p.delete("modal");
      return p;
    });

  const submit = (mode: "cortexgrip" | "custom", list: string[], done: () => void) => {
    setError(null);
    run(() => {
      setBusy(true);
      return domainsApi.setNameservers(orgId, d.id, mode, list).finally(() => setBusy(false));
    }).then(
      async () => {
        await qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
        done();
      },
      (err: unknown) => setError(err),
    );
  };

  const current = d.nameservers.length
    ? d.nameservers
    : d.nameserver_mode === "cortexgrip"
      ? detail.default_nameservers
      : [];
  const incoming = opNameservers(pending).filter((h) => !current.includes(h));
  const site = d.site?.name;

  return (
    <>
      <TwoColumns
        main={
          <>
            {pending && (
              <Notice tone="processing" title="Updating nameservers">
                The registry is switching {d.display_name} to{" "}
                {pending.detail.mode === "cortexgrip" ? "CortexGrip nameservers" : "your custom nameservers"}.
                This usually takes a few minutes; changes can take up to 48 hours to reach everyone.
                {pending.detail.delayed === true
                  ? " It is taking longer than usual: support has been told and is checking it. Your nameservers have not changed yet."
                  : ""}
              </Notice>
            )}
            {!pending && lastFailed && (
              <Notice tone="danger" title="The last nameserver change didn't go through">
                {lastFailed.failure ?? "The registry rejected it."} Your nameservers weren't changed.
              </Notice>
            )}
            <Section
              title={
                <>
                  Nameservers <HelpHint helpKey={HELP_KEYS.domains.nameservers} label="nameservers" />
                </>
              }
              action={
                has(detail, "nameservers") &&
                d.nameserver_mode === "custom" &&
                !pending && (
                  <Button
                    size="lg"
                    variant="secondary"
                    requires={manage}
                    onClick={() => (setError(null), setRevertOpen(true))}
                  >
                    Use CortexGrip nameservers
                  </Button>
                )
              }
            >
              {current.length === 0 && incoming.length === 0 && (
                <p className="text-sm text-fg-subtle">No nameservers are set for this domain.</p>
              )}
              {current.map((h, i) => {
                const replaced = Boolean(pending) && !opNameservers(pending).includes(h);
                return (
                  <RuleRow key={h} first={i === 0}>
                    <p className="min-w-0 flex-1 text-sm break-all text-fg">{h}</p>
                    <Badge tone={replaced ? "neutral" : "success"} size="sm">
                      {replaced ? "Being replaced" : "Active"}
                    </Badge>
                  </RuleRow>
                );
              })}
              {incoming.map((h, i) => (
                <RuleRow key={h} first={current.length === 0 && i === 0}>
                  <p className="min-w-0 flex-1 text-sm break-all text-fg">{h}</p>
                  <Badge tone="warning" size="sm">
                    Pending
                  </Badge>
                </RuleRow>
              ))}
            </Section>
            <ChildNameserversSection detail={detail} />
          </>
        }
        side={
          <>
            <section className="flex flex-col gap-2 rounded-2xl border border-[#f4b53f] bg-[#fef4e5] p-4 shadow-[0_6px_10px_rgba(26,20,18,0.08)]">
              <p className="text-base font-semibold text-fg">What this affects</p>
              <p className="text-[13px] text-[#5c5551]">
                {d.nameserver_mode === "cortexgrip"
                  ? `Switching nameservers moves DNS control away from CortexGrip. ${
                      site ? `Your website (${site}) and anything else` : "Anything"
                    } on this domain depends on DNS records staying correct — you'd need to recreate the ${
                      detail.record_count
                    } ${detail.record_count === 1 ? "record" : "records"} at the new provider.${
                      detail.email_in_use
                        ? " Email delivered through this domain would stop until the new provider has your mail (MX) records."
                        : ""
                    }`
                  : `DNS is hosted at your other provider, so CortexGrip can't change records for you. ${
                      site
                        ? `Your website (${site}) keeps working only while that provider's records point to it.`
                        : ""
                    } Switching back to CortexGrip nameservers lets CortexGrip manage records again.`}
              </p>
            </section>
            <Section title="Change history">
              {events.isPending ? (
                <p className="text-[13px] text-fg-subtle">Loading…</p>
              ) : events.isError ? (
                <ErrorAlert error={events.error} />
              ) : events.data.events.length === 0 ? (
                <p className="text-[13px] text-fg-subtle">No changes yet.</p>
              ) : (
                events.data.events.map((e, i) => (
                  <RuleRow key={e.id} first={i === 0}>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] text-fg">{e.summary}</p>
                      <p className="text-[11px] text-fg-subtle">
                        {longDate(e.created_at)}
                        {e.actor_email ? ` · ${e.actor_email}` : ""}
                      </p>
                    </div>
                  </RuleRow>
                ))
              )}
            </Section>
          </>
        }
      />

      {customOpen && (
        <CustomNameserversModal
          initial={d.nameserver_mode === "custom" ? d.nameservers : []}
          fqdn={d.display_name}
          impact={{ site, email: d.nameserver_mode === "cortexgrip" && Boolean(detail.email_in_use) }}
          busy={busy}
          error={error}
          onClose={() => {
            setError(null);
            closeCustom();
          }}
          onSave={(list) => submit("custom", list, closeCustom)}
        />
      )}
      <Modal
        open={revertOpen}
        onClose={() => setRevertOpen(false)}
        title="Use CortexGrip nameservers?"
        subtitle={detail.default_nameservers.join(" · ")}
        note="Changes can take up to 48 hours to reach everyone."
        footer={
          <>
            <Button variant="secondary" size="lg" onClick={() => setRevertOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="brand"
              size="lg"
              loading={busy}
              onClick={() => submit("cortexgrip", detail.default_nameservers, () => setRevertOpen(false))}
            >
              Switch to CortexGrip
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-[#5c5551]">
          CortexGrip will answer DNS for {d.display_name} again, using the records listed on the DNS tab.
          Records you added at your other provider aren't copied over — check the DNS tab after switching.
        </p>
        <ErrorAlert error={error} />
      </Modal>
      {stepUp}
    </>
  );
}

function CustomNameserversModal({
  initial,
  fqdn,
  impact,
  busy,
  error,
  onClose,
  onSave,
}: {
  initial: string[];
  fqdn: string;
  /** What is live on the domain today that moving its nameservers away would take offline. */
  impact: { site?: string; email: boolean };
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onSave: (list: string[]) => void;
}) {
  const [values, setValues] = useState<string[]>(() => [0, 1, 2, 3].map((i) => initial[i] ?? ""));
  const cleaned = values.map((v) => v.trim().toLowerCase().replace(/\.$/, ""));
  // Empty required ones are the Form's to report; these are the rules the browser can't check.
  const check = () =>
    Object.fromEntries(
      cleaned.map((v, i) => [
        `nameservers.${i}`,
        !v
          ? undefined
          : !NS.test(v)
            ? `Nameserver ${i + 1}: enter a hostname like ns1.yourprovider.com`
            : cleaned.indexOf(v) !== i
              ? `Nameserver ${i + 1} is already listed`
              : undefined,
      ]),
    );

  const risky = Boolean(impact.site) || impact.email;
  const [understood, setUnderstood] = useState(false);
  const edited = values.some((v, i) => v !== (initial[i] ?? ""));
  const unsaved = useUnsavedChanges(edited && !busy, { ignoreSearch: true });
  const close = () => unsaved.guard(onClose);

  return (
    <>
      <Modal open onClose={close} width={640} title="Use custom nameservers">
        <Form
          className="flex flex-col gap-4"
          error={error}
          validate={check}
          onSubmit={(e) => {
            e.preventDefault();
            if (risky && !understood) return;
            onSave(cleaned.filter(Boolean));
          }}
        >
          <p className="text-[13px] text-[#766e6a]">
            Point this domain to nameservers you manage elsewhere. Once switched, CortexGrip can no longer
            manage DNS records here — website and email may stop resolving until the new nameservers are
            configured correctly.
          </p>
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            {values.map((v, i) => (
              <Field
                key={i}
                label={`Nameserver ${i + 1}`}
                name={`nameservers.${i}`}
                required={i < 2}
                optional={i >= 2}
              >
                {(control) => (
                  <Input
                    size="lg"
                    {...control}
                    value={v}
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={i < 2 ? `ns${i + 1}.yourprovider.com` : undefined}
                    onChange={(e) => setValues((prev) => prev.map((p, j) => (j === i ? e.target.value : p)))}
                  />
                )}
              </Field>
            ))}
          </div>
          {risky && (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-xl border border-[#f4b53f] bg-[#fef4e5] p-3 text-[13px] text-[#5c5551]"
            >
              <p className="font-semibold text-fg">This will affect what is live on {fqdn}</p>
              <ul className="list-disc pl-5">
                {impact.site && (
                  <li>
                    Your website ({impact.site}) will stop loading until the new provider has the same
                    records.
                  </li>
                )}
                {impact.email && (
                  <li>
                    Email sent to this domain will stop arriving until the new provider has your mail (MX)
                    records.
                  </li>
                )}
              </ul>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={understood}
                  onChange={(e) => setUnderstood(e.target.checked)}
                  className="mt-0.5"
                />
                <span>I have copied these records to my new provider, or I accept the interruption.</span>
              </label>
            </div>
          )}
          <ErrorAlert error={error} />
          <div className="flex flex-wrap gap-2.5">
            <Button
              variant="brand"
              size="lg"
              type="submit"
              loading={busy}
              disabledReason={
                risky && !understood ? "Tick the box to confirm you understand the impact" : undefined
              }
            >
              Save changes
            </Button>
            <Button size="lg" variant="secondary" onClick={close}>
              Cancel
            </Button>
          </div>
        </Form>
      </Modal>
      {unsaved.modal}
    </>
  );
}
